/**
 * Sends client-side crashes to the existing bug-report endpoint.
 *
 * Before this, a crash showed the user an error screen and left no trace
 * anywhere: the failure is client-side, so it never reaches Vercel or Google
 * Cloud logs. Every report arrived as second-hand description instead of a
 * stack trace, which is why the same iOS problem survived several fix attempts.
 *
 * Deliberately dependency-free at module scope. This runs from inside the error
 * boundaries, so it has to work even when the module that crashed is the one
 * that would normally provide helpers.
 */

import { describeJobContext } from './job-context';

export type CrashSource = 'route-error' | 'global-error';

const REPORT_ENDPOINT = '/api/bug-report';
const MAX_DESCRIPTION_LENGTH = 2000;

/** Crashes already sent this session, so a re-rendered boundary does not duplicate. */
const sentCrashKeys = new Set<string>();

function crashKey(source: CrashSource, error: Error & { digest?: string }): string {
  return [source, error.name, error.message, error.digest ?? '', (error.stack ?? '').slice(0, 400)].join('|');
}

function describeDevice(): string {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'Device: unknown (no window)';
  }

  const lines = [`User agent: ${navigator.userAgent}`, `URL: ${window.location.href}`];

  try {
    lines.push(`Online: ${navigator.onLine ? 'yes' : 'no'}`);
    lines.push(`Viewport: ${window.innerWidth}x${window.innerHeight}`);
    lines.push(`Screen: ${window.screen.width}x${window.screen.height}`);
    lines.push(`Pixel ratio: ${window.devicePixelRatio}`);
    lines.push(`Standalone: ${window.matchMedia('(display-mode: standalone)').matches ? 'yes' : 'no'}`);
  } catch {
    // Any of these can throw in a locked-down browser; partial info still helps.
  }

  return lines.join('\n');
}

/** Pulled in lazily so a broken logger module cannot stop the crash report. */
async function collectAppLogs(): Promise<string> {
  try {
    const { logger } = await import('./logger');
    // Automatic upload, so document names are withheld. The manual bug-report
    // form still sends them, because there the user opted in.
    return logger.exportLogs({ redactDocumentNames: true });
  } catch {
    return '(app logs unavailable)';
  }
}

function buildDescription(source: CrashSource, error: Error & { digest?: string }): string {
  const digest = error.digest ? ` [digest ${error.digest}]` : '';
  const label = source === 'global-error' ? 'Awaria aplikacji' : 'Awaria ekranu';
  return `[AUTO] ${label}: ${error.name}: ${error.message}${digest}`.slice(0, MAX_DESCRIPTION_LENGTH);
}

async function buildLogs(source: CrashSource, error: Error & { digest?: string }): Promise<string> {
  const appLogs = await collectAppLogs();

  return [
    '=== Crash ===',
    `Source: ${source}`,
    `Name: ${error.name}`,
    `Message: ${error.message}`,
    error.digest ? `Digest: ${error.digest}` : null,
    '',
    '=== Stack ===',
    error.stack ?? '(no stack available)',
    '',
    '=== Job ===',
    describeJobContext(),
    '',
    '=== Device ===',
    describeDevice(),
    '',
    appLogs,
  ]
    .filter((line) => line !== null)
    .join('\n');
}

/**
 * Reports a crash and resolves with the report ID, or null when reporting
 * failed. Never throws: a failed report must not replace the error screen the
 * user is looking at with a second crash.
 */
export async function sendCrashReport(
  source: CrashSource,
  error: Error & { digest?: string }
): Promise<string | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  const key = crashKey(source, error);
  if (sentCrashKeys.has(key)) {
    return null;
  }
  sentCrashKeys.add(key);

  try {
    const response = await fetch(REPORT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        description: buildDescription(source, error),
        logs: await buildLogs(source, error),
        context: { screen: `crash:${source}`, fileType: '' },
      }),
    });

    if (!response.ok) {
      // Allow a retry via the button if the server rejected this attempt.
      sentCrashKeys.delete(key);
      return null;
    }

    const data = (await response.json()) as { id?: string };
    return data.id ?? null;
  } catch {
    sentCrashKeys.delete(key);
    return null;
  }
}

/** Clears the dedupe cache. Exists for tests. */
export function resetCrashReportCache(): void {
  sentCrashKeys.clear();
}
